import React, { useEffect, useMemo, useState } from "react";
import { Plus, RefreshCw } from "lucide-react";
import { useVehicleCategoryStore } from "../../store/useVehicleCategoryStore";
import type { VehicleCategory } from "../../types/vehicleCategory";
import { VehicleCategoryModal } from "./VehicleCategoryModal";
import { LoadingSpinner } from "../common/LoadingSpinner";

export const VehicleCategoriesManagement: React.FC = () => {
  const {
    categories,
    loading,
    isRefreshing,
    loadIfNeeded,
    refreshCategories,
    addCategory,
    updateCategory,
    removeCategory,
  } = useVehicleCategoryStore();
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit" | "delete">(
    "create",
  );
  const [selectedCategory, setSelectedCategory] =
    useState<VehicleCategory | null>(null);

  useEffect(() => {
    loadIfNeeded().catch(console.error);
  }, [loadIfNeeded]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return categories;
    return categories.filter(
      (category) =>
        category.category_id.toLowerCase().includes(q) ||
        category.category_name.toLowerCase().includes(q),
    );
  }, [categories, search]);

  const handleSave = async (payload: Record<string, unknown>) => {
    if (modalMode === "create") {
      await addCategory(
        payload as Omit<VehicleCategory, "created_at" | "updated_at">,
      );
    } else if (modalMode === "edit" && selectedCategory) {
      await updateCategory(selectedCategory.category_id, payload);
    } else if (modalMode === "delete" && selectedCategory) {
      await removeCategory(selectedCategory.category_id);
    }
    setModalOpen(false);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">
            Vehicle Categories
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage vehicle categories used by bike records and reports
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refreshCategories().catch(console.error)}
            className="px-4 py-2 text-sm border border-gray-300 rounded-md bg-white hover:bg-gray-50 flex items-center gap-2"
            disabled={isRefreshing}
          >
            <RefreshCw
              className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
            />
            Refresh
          </button>
          <button
            onClick={() => {
              setSelectedCategory(null);
              setModalMode("create");
              setModalOpen(true);
            }}
            className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Category
          </button>
        </div>
      </div>

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by category ID or name..."
        className="w-full md:w-96 px-3 py-2 border border-gray-300 rounded-md text-sm"
      />

      {loading && categories.length === 0 ? (
        <div className="py-12 flex justify-center">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Category ID
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Category Name
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Color
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Max Speed
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filtered.map((category) => (
                <tr key={category.category_id}>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {category.category_id}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {category.category_name}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {category.color ? (
                      <div className="flex items-center gap-2">
                        <span
                          className="inline-block h-4 w-4 rounded border border-gray-300"
                          style={{ backgroundColor: category.color }}
                        />
                        <span className="font-mono text-xs uppercase">
                          {category.color}
                        </span>
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {category.max_speed_kmh != null
                      ? `${category.max_speed_kmh} km/h`
                      : "-"}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => {
                          setSelectedCategory(category);
                          setModalMode("edit");
                          setModalOpen(true);
                        }}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => {
                          setSelectedCategory(category);
                          setModalMode("delete");
                          setModalOpen(true);
                        }}
                        className="text-red-600 hover:text-red-800"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-8 text-center text-sm text-gray-500"
                  >
                    No categories found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <VehicleCategoryModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        mode={modalMode}
        category={selectedCategory}
        onSave={handleSave}
      />
    </div>
  );
};
